import { CommonModule } from '@angular/common';
import { Component, computed, input, model } from '@angular/core';

interface PaginationItem {
  display: string | number;
  isCurrent: boolean;
  jumpTo?: number; // Not covered by share
}

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
})
export class PaginationComponent {
  public numberOfPages = input<number | undefined>(undefined);

  public selectedPage = model<number>(this.numberOfPages() ?? 1);

  protected paginationItems = computed((): PaginationItem[] => {
    const numberOfPages = this.numberOfPages();
    const selectedPage = this.selectedPage();

    if (numberOfPages == undefined) {
      return [];
    }
    return this.getPaginationItems(numberOfPages, selectedPage);
  });

  protected havePrevPageNum = computed(() => this.selectedPage() > 1);
  protected haveNextPageNum = computed(() => {
    const total = this.numberOfPages();
    return total !== undefined && this.selectedPage() < total;
  });

  navigateToPage(item: PaginationItem) {
    if (typeof item.display === 'number') {
      this.selectedPage.set(item.display);
    } else if (item.jumpTo !== undefined) {
      this.selectedPage.set(item.jumpTo);
    }
  }
  navigateToNext() {
    this.selectedPage.set(this.selectedPage() + 1);
  }
  navigateToPrev() {
    this.selectedPage.set(this.selectedPage() - 1);
  }

  private getPaginationItems(
    pageCount: number,
    currentPage: number,
  ): PaginationItem[] {
    const paginationItems: PaginationItem[] = [];

    if (pageCount <= 4) {
      // Simply show all pages 1..pageCount, no ellipses
      for (let i = 1; i <= pageCount; i++) {
        paginationItems.push({
          display: i,
          isCurrent: i === currentPage,
        });
      }
    } else {
      //  pageCount > 4: Show advanced pagination with possible ellipses

      //
      // 1. Possibly show the first page + "previous" ellipses
      //
      // Only show if currentPage > 2 so that we “skip” pages before the visible window.
      if (currentPage > 2) {
        paginationItems.push({
          display: 1,
          isCurrent: currentPage === 1,
        });

        // If currentPage > 3, there’s a gap after page 1, so show “...”
        if (currentPage > 3) {
          paginationItems.push({
            display: '...',
            isCurrent: false,
            jumpTo: currentPage - 2,
          });
        }
      }

      //
      // 2. Add the "middle" window of pages around currentPage
      //
      // E.g., show [currentPage - 1, currentPage, currentPage + 1] within valid bounds
      const startPage = Math.max(1, currentPage - 1);
      const endPage = Math.min(pageCount, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        paginationItems.push({
          display: i,
          isCurrent: i === currentPage,
        });
      }

      //
      // 3. Possibly show the last page + "next" ellipses
      //
      // Only show if currentPage < (pageCount - 1) so that we “skip” pages after the visible window.
      if (currentPage < pageCount - 1) {
        // If currentPage < (pageCount - 2), show a gap before the final page
        if (currentPage < pageCount - 2) {
          paginationItems.push({
            display: '...',
            isCurrent: false,
            jumpTo: currentPage + 2,
          });
        }
        paginationItems.push({
          display: pageCount,
          isCurrent: currentPage === pageCount,
        });
      }
    }

    return paginationItems;
  }
}
